import { Injectable } from '@angular/core';
import { RequestService } from 'angular/resources/request.service';
import { RestResourceModule } from 'angular/resources/rest-resource.module';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: RestResourceModule
})
export class SsoSettingsResource {
    private readonly restUrl = 'sso-settings';

    constructor(private requestService: RequestService) {}

    public toggleAdvancedSettings(
        ssoRefId: number,
        enabled: boolean
    ): Observable<StorageAdvancedSettingsDto> {
        return this.requestService.post<boolean>(
            `${this.restUrl}/${ssoRefId}/storage-settings/advanced-settings`,
            enabled
        ) as unknown as Observable<StorageAdvancedSettingsDto>;
    }

    public fetchAdvancedSettings(
        ssoRefId: number
    ): Observable<StorageAdvancedSettingsDto> {
        return this.requestService.get<StorageAdvancedSettingsDto>(
            `${this.restUrl}/${ssoRefId}/storage-settings/advanced-settings`
        );
    }

    public saveMaxBidsPerShipper(
        ssoRefId: number,
        value: number
    ): Observable<number> {
        return this.requestService.post<number>(
            `${this.restUrl}/${ssoRefId}/storage-settings/max-bids-per-shipper`,
            value
        );
    }

    public saveMinQuantity(
        ssoRefId: number,
        minQuantity: StorageMinQuantityDto
    ) {
        return this.requestService.post<StorageMinQuantityDto>(
            `${this.restUrl}/${ssoRefId}/storage-settings/min-quantity`,
            minQuantity
        );
    }

    public deleteMinQuantity(ssoRefId: number, id: number) {
        return this.requestService.delete(
            `${this.restUrl}/${ssoRefId}/storage-settings/min-quantity/${id}`
        );
    }

    public fetchContractUpgradePeriod(
        ssoRefId: number
    ): Observable<StoragePeriodType[]> {
        return this.requestService.get<StoragePeriodType[]>(
            `${this.restUrl}/${ssoRefId}/storage-settings/contract-upgrade-period`
        );
    }

    public saveProductPeriodForUpgrade(periodType: StoragePeriodType) {
        return this.requestService.post(
            `${this.restUrl}/storage-settings/contract-upgrade-period`,
            periodType
        );
    }

    public deleteProductPeriodForUpgrade(periodType: StoragePeriodType) {
        return this.requestService.delete(
            `${this.restUrl}/storage-settings/contract-upgrade-period/${periodType}`
        );
    }

    public saveProductPeriodForManualAllocation(periodType: StoragePeriodType) {
        return this.requestService.post(
            `${this.restUrl}/storage-settings/manual-allocation`,
            periodType
        );
    }

    public deleteProductPeriodForManualAllocation(
        periodType: StoragePeriodType
    ) {
        return this.requestService.delete(
            `${this.restUrl}/storage-settings/manual-allocation/${periodType}`
        );
    }

    public toggleCreditLimitCheckSetting(
        ssoRefId: number,
        enabled: boolean
    ): Observable<boolean> {
        return this.requestService.post<boolean>(
            `${this.restUrl}/${ssoRefId}/storage-settings/credit-limit-check`,
            enabled
        );
    }

    public fetchCreditLimitCheckSetting(
        ssoRefId: number
    ): Observable<StorageCreditLimitCheckSettingDto> {
        return this.requestService.get<StorageCreditLimitCheckSettingDto>(
            `${this.restUrl}/${ssoRefId}/storage-settings/credit-limit-check`
        );
    }

    public saveCreditFramework(
        ssoRefId: number,
        framework: StorageFrameworkCreationDto
    ): Observable<StorageFrameworkDto> {
        return this.requestService.post<StorageFrameworkCreationDto>(
            `${this.restUrl}/${ssoRefId}/storage-settings/credit-framework`,
            framework
        ) as Observable<StorageFrameworkDto>;
    }

    public updateCreditFramework(
        ssoRefId: number,
        framework: StorageFrameworkDto
    ): Observable<StorageFrameworkDto> {
        return this.requestService.post<StorageFrameworkDto>(
            `${this.restUrl}/${ssoRefId}/storage-settings/credit-framework/${framework.id}`,
            framework as StorageFrameworkDto
        );
    }

    public deleteCreditFramework(
        ssoRefId: number,
        creditFrameworkId: number
    ): Observable<void> {
        return this.requestService.delete(
            `${this.restUrl}/${ssoRefId}/storage-settings/credit-framework/${creditFrameworkId}`
        );
    }

    public queryAllocationServiceSubscription(
        ssoRefId: number
    ): Observable<StoragePremiumServiceSubscriptionCreationDto> {
        return this.requestService.get<StoragePremiumServiceSubscriptionCreationDto>(
            `${this.restUrl}/${ssoRefId}/storage-settings/allocation-service-subscription`
        );
    }
}
